// One sideways-scrolling row (option cards, days, start times) instead of a
// row that wraps and pushes the panel down. While part of the row is hidden
// on a side, that side fades out and shows an arrow button, so a guest sees
// there is more and can reach it with a mouse (a wheel only scrolls down);
// a finger or trackpad just swipes. The selected item is kept in view, and
// only this row scrolls to show it — never the page.
//
// Paged (`paged`): when the ROW itself is narrower than `paged.below` — a
// phone, or a widget embedded in a narrow column of a wide page — it shows
// exactly ONE item at a time at its full width (owner, 25 Sept: "I would
// prefer one card on screen at a time on mobile"): it snaps item by item (a
// swipe moves one), with no fade and no sliver of the next item, and a pager
// under it — the prev/next arrows, a dot per item (buttons, named) and
// "2 of 4", announced politely once the row settles (a guest's swipe, arrow
// or dot; not a move made for focus). With more dots than one line holds,
// they wrap between the arrows. Focus that lands in an item off to the side
// (Tab, or a control the panel focuses) brings that item in. Wider rows are
// unchanged.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type ReactNode,
} from "react";

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

/** Hidden space at an edge that still counts as "more" (sub-pixel widths). */
const EDGE_PX = 2;

function Chevron({ dir }: { dir: "left" | "right" }) {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        d={dir === "right" ? "M7.5 4.5 13 10l-5.5 5.5" : "M12.5 4.5 7 10l5.5 5.5"}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The row snaps (scroll-snap-type: x proximity), so a scroll that leaves an
 * item 56 px from the edge is pulled to the nearest item's start — which can
 * put the item back under a fade. Aim instead at the snap position (an item's
 * start, or either end) nearest `desired` that keeps the item clear of both
 * edges; with none, `desired` as before.
 */
function snapClear(t: HTMLElement, r: DOMRect, pad: number, desired: number): number {
  const tr = t.getBoundingClientRect();
  const max = Math.max(0, t.scrollWidth - t.clientWidth);
  const padStart = parseFloat(getComputedStyle(t).scrollPaddingInlineStart) || 0;
  const items = Array.from(t.firstElementChild?.children ?? []).filter(
    (c) => getComputedStyle(c).scrollSnapAlign.includes("start"),
  );
  const candidates = [0, max, ...items.map((c) => t.scrollLeft + c.getBoundingClientRect().left - tr.left - padStart)];
  let best: number | null = null;
  for (const raw of candidates) {
    const left = Math.min(max, Math.max(0, raw));
    const shift = left - t.scrollLeft;
    const clear = r.left - shift >= tr.left + pad && r.right - shift <= tr.right - pad;
    if (clear && (best == null || Math.abs(left - desired) < Math.abs(best - desired))) best = left;
  }
  return best ?? desired;
}

/** The row's items: the children of the element the track holds. */
const itemsOf = (t: HTMLElement): HTMLElement[] => Array.from((t.firstElementChild?.children ?? []) as HTMLCollectionOf<HTMLElement>);

/** Where the track scrolls to put item `el` at its start (its snap position). */
function itemLeft(t: HTMLElement, el: Element): number {
  const padStart = parseFloat(getComputedStyle(t).scrollPaddingInlineStart) || 0;
  const max = Math.max(0, t.scrollWidth - t.clientWidth);
  return Math.min(max, Math.max(0, t.scrollLeft + el.getBoundingClientRect().left - t.getBoundingClientRect().left - padStart));
}

/** The item whose start is nearest the track's start (paged: the one on show). */
function currentIndex(t: HTMLElement): number {
  const items = itemsOf(t);
  let best = 0;
  let bestD = Infinity;
  items.forEach((el, i) => {
    const d = Math.abs(itemLeft(t, el) - t.scrollLeft);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  });
  return best;
}

/** Paged mode: one item per view below `below` px of row width. */
export type HScrollerPaging = {
  /** Row width (px) below which one item shows at a time. */
  below: number;
  /** Arrow names while paged ("Previous option" / "Next option"). */
  prevLabel: string;
  nextLabel: string;
  /** The dots' group name ("Options"). */
  groupLabel: string;
  /** Item `i`'s name, for its dot and the "2 of 4" announcement ("To the Top"). */
  itemLabel: (i: number) => string;
  /** Item `i` is chosen: its dot says so (a ring, and ", chosen" in its name), so a choice scrolled away still shows. */
  itemChosen?: (i: number) => boolean;
};

/** A position settles this long after the last scroll event before it is announced. */
const SETTLE_MS = 180;
/** A move the row makes by itself stays unannounced if it settles within this (see `spoken`). */
const QUIET_MS = 1500;

export function HScroller({
  className,
  prevLabel,
  nextLabel,
  revealSelector,
  revealKey,
  focusRevealSelector,
  paged: paging,
  children,
}: {
  className?: string;
  /** Arrow button names: say what is there ("Show later start times"). */
  prevLabel: string;
  nextLabel: string;
  /** The selected item inside the row, kept in view whenever revealKey changes. */
  revealSelector?: string;
  revealKey?: string | number | null;
  /** The item a focused control belongs to (its day tile), revealed whole on keyboard focus. */
  focusRevealSelector?: string;
  /** One item per view while the row is narrower than `below` (see the file header). */
  paged?: HScrollerPaging;
  children: ReactNode;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ before: false, after: false });
  // Paged: whether the row is narrow enough, how many items, which is on
  // show, and which one the live region last announced (after settling).
  const [isPaged, setIsPaged] = useState(false);
  const [count, setCount] = useState(0);
  const [index, setIndex] = useState(0);
  const [settled, setSettled] = useState(0);
  const settledRef = useRef(0);
  settledRef.current = settled;
  // What the polite live region says. A move the row makes by itself
  // (focus landing in another item, the selected item revealed) is left
  // unsaid: the focus or the choice is already being announced, so the
  // region is emptied instead (which says nothing), until a guest's own
  // swipe, arrow or dot moves the row again.
  const [spoken, setSpoken] = useState(true);
  const quietUntil = useRef(0);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const below = paging?.below ?? 0;
  const isPagedRef = useRef(false);
  isPagedRef.current = isPaged;

  const measure = useCallback(() => {
    const t = trackRef.current;
    const root = rootRef.current;
    if (!t) return;
    const hidden = t.scrollWidth - t.clientWidth;
    const before = hidden > EDGE_PX && t.scrollLeft > EDGE_PX;
    const after = hidden > EDGE_PX && t.scrollLeft < hidden - EDGE_PX;
    setEdges((p) => (p.before === before && p.after === after ? p : { before, after }));
    // The ROW's own width decides, never the window's: an embedded widget
    // in a narrow column pages just as a phone does.
    const narrow = below > 0 && !!root && root.getBoundingClientRect().width < below;
    setIsPaged(narrow);
    setCount(itemsOf(t).length);
    const i = currentIndex(t);
    setIndex(i);
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      setSettled(currentIndex(t));
      const quiet = Date.now() < quietUntil.current;
      quietUntil.current = 0;
      setSpoken(!quiet);
    }, SETTLE_MS);
  }, [below]);

  useLayoutEffect(() => {
    const t = trackRef.current;
    if (!t) return;
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(t);
    if (rootRef.current) ro?.observe(rootRef.current);
    const mo = typeof MutationObserver !== "undefined" ? new MutationObserver(measure) : null;
    mo?.observe(t, { childList: true, subtree: true });
    t.addEventListener("scroll", measure, { passive: true });
    return () => {
      ro?.disconnect();
      mo?.disconnect();
      t.removeEventListener("scroll", measure);
      if (settleTimer.current) clearTimeout(settleTimer.current);
    };
  }, [measure]);

  // Paged: bring item `i` in (smooth; at once under prefers-reduced-motion).
  const goTo = useCallback((i: number, quiet = false) => {
    const t = trackRef.current;
    if (!t) return;
    const items = itemsOf(t);
    const el = items[Math.max(0, Math.min(items.length - 1, i))];
    if (!el) return;
    if (quiet) quietUntil.current = Date.now() + QUIET_MS;
    t.scrollTo({ left: itemLeft(t, el), behavior: reducedMotion() ? "auto" : "smooth" });
  }, []);
  const itemIndexOf = (el: Element): number => {
    const t = trackRef.current;
    if (!t) return -1;
    return itemsOf(t).findIndex((it) => it === el || it.contains(el));
  };

  // Scroll only this row so `el` sits clear of the faded edges, never under
  // an arrow. Paged: bring in the item it belongs to.
  const reveal = useCallback((el: Element) => {
    const t = trackRef.current;
    if (!t) return;
    if (isPagedRef.current) {
      const i = itemsOf(t).findIndex((it) => it === el || it.contains(el));
      if (i >= 0 && Math.abs(itemLeft(t, itemsOf(t)[i]!) - t.scrollLeft) > 1) goTo(i, true);
      return;
    }
    const pad = 56;
    const tr = t.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    let delta = 0;
    if (r.left < tr.left + pad) delta = r.left - tr.left - pad;
    else if (r.right > tr.right - pad) delta = r.right - tr.right + pad;
    if (Math.abs(delta) < 1) return;
    t.scrollTo({ left: snapClear(t, r, pad, t.scrollLeft + delta), behavior: reducedMotion() ? "auto" : "smooth" });
  }, [goTo]);

  useEffect(() => {
    const t = trackRef.current;
    if (!t || !revealSelector || revealKey == null) return;
    const el = t.querySelector<HTMLElement>(revealSelector);
    if (el) reveal(el);
  }, [revealKey, revealSelector, reveal]);

  // Keyboard focus that lands on an item peeking at an edge (Tab through the
  // start buttons of the day tiles) brings its item into view, so the focus
  // ring is never under the fade or an arrow. Only keyboard focus
  // (:focus-visible): a tap already sees what it tapped. Opt-in per row
  // (focusRevealSelector): the other rows keep the browser's own scrolling.
  const onFocus = (e: FocusEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    // Paged: whatever takes focus (Tab, a tap, or a control the panel
    // focuses without scrolling) brings in its item.
    if (isPaged) {
      const i = itemIndexOf(target);
      const t = trackRef.current;
      // The browser's own focus scrolling may already have moved the row
      // before this runs; either way the move to another item is focus's
      // doing, not the guest's swipe, and is left unsaid.
      if (i >= 0 && i !== settledRef.current) quietUntil.current = Date.now() + QUIET_MS;
      if (t && i >= 0 && Math.abs(itemLeft(t, itemsOf(t)[i]!) - t.scrollLeft) > 1) goTo(i, true);
      return;
    }
    if (!focusRevealSelector) return;
    let visible = true;
    try {
      visible = target.matches(":focus-visible");
    } catch {
      /* an engine without :focus-visible: reveal anyway */
    }
    if (!visible) return;
    const item = target.closest(focusRevealSelector) || target;
    reveal(e.currentTarget.contains(item) ? item : target);
  };

  const page = (dir: 1 | -1) => {
    const t = trackRef.current;
    if (!t) return;
    if (isPaged) {
      goTo(currentIndex(t) + dir);
      return;
    }
    t.scrollBy({ left: dir * Math.max(120, Math.round(t.clientWidth * 0.8)), behavior: reducedMotion() ? "auto" : "smooth" });
  };

  const pager = isPaged && paging && count > 1;
  // The arrows are pointer shortcuts: keyboard focus moves along the row
  // itself (arrow keys in a radio group, Tab through the cards) and the row
  // scrolls to follow it, so they stay out of the Tab order. Paged, the dots
  // are the keyboard's way along the row.
  return (
    <div
      ref={rootRef}
      className={`vt-hs${className ? ` ${className}` : ""}`}
      data-paged={isPaged || undefined}
      // Paged: how many items, so the row's content box can be exactly N
      // track-widths wide and the last item snaps flush (styles.css).
      style={isPaged ? ({ "--vt-hs-count": Math.max(1, count) } as CSSProperties) : undefined}
      data-more-before={(!isPaged && edges.before) || undefined}
      data-more-after={(!isPaged && edges.after) || undefined}
    >
      <div ref={trackRef} className="vt-hs-track" onFocus={onFocus}>
        {children}
      </div>
      {!isPaged && edges.before && (
        <button type="button" className="vt-hs-btn vt-hs-prev" aria-label={prevLabel} tabIndex={-1} onClick={() => page(-1)}>
          <Chevron dir="left" />
        </button>
      )}
      {!isPaged && edges.after && (
        <button type="button" className="vt-hs-btn vt-hs-next" aria-label={nextLabel} tabIndex={-1} onClick={() => page(1)}>
          <Chevron dir="right" />
        </button>
      )}
      {pager && (
        <div className="vt-hs-pager">
          <button
            type="button"
            className="vt-hs-btn vt-hs-prev"
            aria-label={paging.prevLabel}
            aria-disabled={index === 0 || undefined}
            tabIndex={-1}
            onClick={() => {
              if (index > 0) page(-1);
            }}
          >
            <Chevron dir="left" />
          </button>
          {/* The middle cell holds the dots and "2 of 4" and wraps them onto
              another line when there are more dots than fit, so the arrows
              always stay at the row's two ends (styles.css). */}
          <div className="vt-hs-mid">
            <div className="vt-hs-dots" role="group" aria-label={paging.groupLabel}>
              {Array.from({ length: count }, (_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`vt-hs-dot${paging.itemChosen?.(i) ? " vt-hs-dot-chosen" : ""}`}
                  aria-label={`${i + 1} of ${count}: ${paging.itemLabel(i)}${paging.itemChosen?.(i) ? ", chosen" : ""}`}
                  aria-current={i === index ? "true" : undefined}
                  onClick={() => goTo(i)}
                />
              ))}
            </div>
            {/* "2 of 4" follows the swipe; what is announced waits until the
                row settles, so a jump from 1 to 4 is announced once. */}
            <p className="vt-hs-pos">
              <span aria-hidden="true">{`${Math.min(index, count - 1) + 1} of ${count}`}</span>
              <span className="vt-avail-sr" aria-live="polite" aria-atomic="true">
                {spoken ? `${Math.min(settled, count - 1) + 1} of ${count}: ${paging.itemLabel(Math.min(settled, count - 1))}` : ""}
              </span>
            </p>
          </div>
          <button
            type="button"
            className="vt-hs-btn vt-hs-next"
            aria-label={paging.nextLabel}
            aria-disabled={index >= count - 1 || undefined}
            tabIndex={-1}
            onClick={() => {
              if (index < count - 1) page(1);
            }}
          >
            <Chevron dir="right" />
          </button>
        </div>
      )}
    </div>
  );
}
